'use client'

import { useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useSearchParams } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalendarIcon,
  DownloadIcon,
  EyeIcon,
  MoreHorizontalIcon,
  DatabaseIcon,
  HardDriveIcon,
  PlayIcon,
  SquareMenuIcon,
  Trash2Icon,
  FolderArchiveIcon,
  ServerIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { DeleteConfirmationDialog, JobStatusBadge, LoadingState } from '@/components/partials'
import { BackupFilters, TriggerBackupDialog } from '@/components/partials/backups'
import { useBackupRuns, useDeleteBackupRun, useBulkDeleteBackupRuns } from '@/hooks'
import { useBackupsStore, useFilesStore } from '@/store'
import { BackupRunsFilterParams, BackupType } from '@/types/backup'
import { JobStatusEnum, SortEnum } from '@/enums'
import { formatDateTime, formatFileSize } from '@/lib/utils'

const BackupList = () => {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()

  const [deleteBackupRunId, setDeleteBackupRunId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false)
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false)

  const selectedBackupRunIds = useBackupsStore(state => state.selectedBackupRunIds)
  const toggleBackupRunSelection = useBackupsStore(state => state.toggleBackupRunSelection)
  const selectAllBackupRuns = useBackupsStore(state => state.selectAllBackupRuns)
  const deselectAllBackupRuns = useBackupsStore(state => state.deselectAllBackupRuns)
  const isBackupRunSelected = useBackupsStore(state => state.isBackupRunSelected)

  const addToDownloadQueue = useFilesStore(state => state.addToDownloadQueue)

  const filters = useMemo<BackupRunsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())
    const statusesParam = params.get('statuses')
    const scheduleIdsParam = params.get('scheduleIds')
    const createdUsersParam = params.get('createdUsers')
    const updatedUsersParam = params.get('updatedUsers')
    const includeDatabase = params.get('includeDatabase')
    const includeStorage = params.get('includeStorage')

    return {
      page: parseInt(params.get('page') || '1', 10),
      size: parseInt(params.get('size') || '20', 10),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || undefined,
      statuses: statusesParam ? statusesParam.split(',').filter(Boolean) : undefined,
      scheduleIds: scheduleIdsParam ? scheduleIdsParam.split(',').filter(Boolean) : undefined,
      includeDatabase: includeDatabase === null ? undefined : includeDatabase === 'true',
      includeStorage: includeStorage === null ? undefined : includeStorage === 'true',
      createdUsers: createdUsersParam ? createdUsersParam.split(',').filter(Boolean) : undefined,
      updatedUsers: updatedUsersParam ? updatedUsersParam.split(',').filter(Boolean) : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  const { data, isLoading, error, refetch, isFetching } = useBackupRuns(filters)
  const { mutate: deleteBackupRunMutate, isPending: isDeleting } = useDeleteBackupRun()
  const { mutate: bulkDeleteMutate, isPending: isBulkDeleting } = useBulkDeleteBackupRuns()

  const totalPages = data?.pages ?? 1
  const currentPage = data?.page ?? filters.page ?? 1
  const pageSize = data?.size ?? filters.size ?? 20

  const updateUrlParams = (newFilters: Partial<BackupRunsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleSelectAll = () => {
    if (data && data.items.length > 0) {
      if (selectedBackupRunIds.length === data.items.length) {
        deselectAllBackupRuns()
      } else {
        selectAllBackupRuns(data.items as BackupType[])
      }
    }
  }

  const confirmDeleteBackupRun = () => {
    if (!deleteBackupRunId) {
      return
    }
    const runId = deleteBackupRunId

    deleteBackupRunMutate(runId, {
      onSuccess: () => {
        const listQueries = queryClient.getQueriesData({ queryKey: ['backups'] })
        listQueries.forEach(([queryKey, oldData]: [any, any]) => {
          if (!oldData || !oldData.items) {
            return
          }
          queryClient.setQueryData(queryKey, {
            ...oldData,
            total: Math.max(0, (oldData.total ?? 1) - 1),
            items: oldData.items.filter((item: any) => item.id !== runId)
          })
        })
        toast.success(t('deleted_successfully'))
        setDeleteBackupRunId(null)
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message ?? t('error_deleting'))
        setDeleteBackupRunId(null)
      }
    })
  }

  const confirmBulkDeleteBackupRuns = () => {
    if (selectedBackupRunIds.length === 0) {
      return
    }
    const ids = [...selectedBackupRunIds]

    bulkDeleteMutate(ids, {
      onSuccess: () => {
        deselectAllBackupRuns()
        const listQueries = queryClient.getQueriesData({ queryKey: ['backups'] })
        listQueries.forEach(([queryKey, oldData]: [any, any]) => {
          if (!oldData || !oldData.items) {
            return
          }
          queryClient.setQueryData(queryKey, {
            ...oldData,
            total: Math.max(0, (oldData.total ?? ids.length) - ids.length),
            items: oldData.items.filter((item: any) => !ids.includes(item.id))
          })
        })
        setBulkDeleteConfirmOpen(false)
      },
      onError: (error: any) => {
        if (error.response?.status === 422) {
          toast.error(error.response.data?.items?.ids || t('error_deleting'))
        } else {
          toast.error(t('error_deleting'))
        }
        setBulkDeleteConfirmOpen(false)
      }
    })
  }

  const renderActionItems = (backup: BackupType, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            router.push(`/backups/${backup.id}`)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('backup_details')}
        </ItemComponent>
        {backup.file && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              addToDownloadQueue(backup.file!)
            }}>
            <DownloadIcon className="h-4 w-4" />
            {t('download')}
          </ItemComponent>
        )}
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            setDeleteBackupRunId(backup.id)
          }}
          className="text-destructive focus:text-destructive">
          <Trash2Icon className="h-4 w-4" />
          {t('delete')}
        </ItemComponent>
      </>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between sm:gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FolderArchiveIcon className="h-5 w-5" />
                {t('backups')}
              </CardTitle>
              <CardDescription>{t('backups_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {selectedBackupRunIds.length > 0 && (
                <Button variant="destructive" onClick={() => setBulkDeleteConfirmOpen(true)} disabled={isBulkDeleting}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedBackupRunIds.length})</span>
                  </div>
                </Button>
              )}
              <Button variant="outline" onClick={() => router.push('/backups/schedules')}>
                <CalendarIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('manage_schedules')}</span>
              </Button>
              <Button onClick={() => setTriggerDialogOpen(true)}>
                <PlayIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('trigger_backup')}</span>
              </Button>
              <BackupFilters
                filters={filters}
                onFiltersChange={updateUrlParams}
                onResetFilters={() => router.push('/backups')}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {data && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={data.items.length > 0 && selectedBackupRunIds.length === data.items.length}
                              onCheckedChange={handleSelectAll}
                              onClick={e => e.stopPropagation()}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead>{t('status')}</TableHead>
                        <TableHead className="text-center">
                          <DatabaseIcon className="mr-1 inline h-4 w-4" />
                          {t('database')}
                        </TableHead>
                        <TableHead className="text-center">
                          <HardDriveIcon className="mr-1 inline h-4 w-4" />
                          {t('storage')}
                        </TableHead>
                        <TableHead className="text-center">{t('schedule')}</TableHead>
                        <TableHead>{t('message')}</TableHead>
                        <TableHead>{t('file')}</TableHead>
                        <TableHead className="text-right">{t('size')}</TableHead>
                        <TableHead>
                          <CalendarIcon className="mr-1 inline h-4 w-4" />
                          {t('created_at')}
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.items.map((backup, index) => (
                          <ContextMenu key={backup.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${index % 2 === 0 ? 'bg-background' : 'bg-muted/25'}`}
                                onClick={() => router.push(`/backups/${backup.id}`)}>
                                <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                                  <Checkbox
                                    checked={isBackupRunSelected(backup.id)}
                                    onCheckedChange={() => toggleBackupRunSelection(backup.id)}
                                    onClick={e => e.stopPropagation()}
                                    className="cursor-pointer"
                                  />
                                </TableCell>
                                <TableCell>
                                  <JobStatusBadge status={backup.status as JobStatusEnum} />
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={backup.includeDatabase ? 'success' : 'outline'} className="text-xs">
                                    {backup.includeDatabase ? t('yes') : t('no')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={backup.includeStorage ? 'success' : 'outline'} className="text-xs">
                                    {backup.includeStorage ? t('yes') : t('no')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  {backup.scheduleId ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 gap-1 px-2 text-xs"
                                      onClick={e => {
                                        e.stopPropagation()
                                        router.push(`/backups/schedules/${backup.scheduleId}`)
                                      }}>
                                      <ServerIcon className="h-3.5 w-3.5" />
                                      {t('view_schedule')}
                                    </Button>
                                  ) : (
                                    <span className="text-muted-foreground text-sm">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-muted-foreground max-w-64 truncate text-sm">
                                  {backup.message
                                    ? t.has(backup.message as any)
                                      ? t(backup.message as any)
                                      : backup.message
                                    : '-'}
                                </TableCell>
                                <TableCell>
                                  {backup.file ? (
                                    <span className="block max-w-40 truncate text-sm">{backup.file.name}</span>
                                  ) : (
                                    <span className="text-muted-foreground text-sm">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-right text-sm tabular-nums">
                                  {backup.file ? formatFileSize(backup.file.size) : '-'}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm">
                                  {formatDateTime(backup.createdAt, locale)}
                                </TableCell>
                                <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm">
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(backup, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(backup, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={data.total}
                  pageSize={pageSize}
                  onPageChangeAction={page => updateUrlParams({ page })}
                  onPageSizeChangeAction={size => updateUrlParams({ size, page: 1 })}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteBackupRunId}
        onCancel={() => setDeleteBackupRunId(null)}
        onConfirm={confirmDeleteBackupRun}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteBackupRuns}
        isDeleting={isBulkDeleting}
      />

      <TriggerBackupDialog open={triggerDialogOpen} onOpenChange={setTriggerDialogOpen} />
    </>
  )
}

export default BackupList
